import type { GuideTourStep } from '../types'
import { GUIDE_TOUR_STEP_POSITIONS } from '../types'
import { assertNonEmptyString } from './assertions'
import { GuideTourValidationError } from './errors'
import { validateOptionalGuideTourExpiresAt } from './expiresAt'
import { validateGuideTourObserverSelectors } from './observerSelectors'
import { validateGuideTourWhenTargetMissing } from './whenTargetMissing'
import { validateGuideTourCardSize } from './validateGuideTourCardSize'

const validateGuideTourStepModalVisible = (
  waitForModalVisible: GuideTourStep['waitForModalVisible'],
  prefix: string
): void => {
  if (waitForModalVisible === undefined) {
    return
  }

  const isValid =
    waitForModalVisible === true ||
    waitForModalVisible === false ||
    (typeof waitForModalVisible === 'string' &&
      waitForModalVisible.trim().length > 0)

  if (!isValid) {
    throw new GuideTourValidationError(
      `${prefix}.waitForModalVisible must be true, false, or a non-empty modal id`
    )
  }
}

const validateOptionalBooleanField = (
  value: unknown,
  fieldName: string
): void => {
  if (value !== undefined && typeof value !== 'boolean') {
    throw new GuideTourValidationError(`${fieldName} must be a boolean`)
  }
}

export const validateGuideTourStepFields = (
  step: GuideTourStep,
  stepIndex: number
): void => {
  const prefix = `steps[${stepIndex}]`

  assertNonEmptyString(step.title, `${prefix}.title`)
  assertNonEmptyString(step.body, `${prefix}.body`)

  if (step.targetId !== undefined) {
    assertNonEmptyString(step.targetId, `${prefix}.targetId`)
  }

  if (step.badge !== undefined) {
    assertNonEmptyString(step.badge, `${prefix}.badge`)
  }

  if (step.route !== undefined) {
    assertNonEmptyString(step.route, `${prefix}.route`)
  }

  if (
    step.position !== undefined &&
    !GUIDE_TOUR_STEP_POSITIONS.includes(step.position)
  ) {
    throw new GuideTourValidationError(
      `${prefix}.position must be one of: ${GUIDE_TOUR_STEP_POSITIONS.join(', ')}`
    )
  }

  validateGuideTourCardSize(step.size, `${prefix}.size`)

  validateGuideTourObserverSelectors(
    step.waitForHidden,
    `${prefix}.waitForHidden`
  )

  validateOptionalBooleanField(
    step.waitForModalHidden,
    `${prefix}.waitForModalHidden`
  )

  validateGuideTourStepModalVisible(step.waitForModalVisible, prefix)

  if (step.forceClick !== undefined) {
    assertNonEmptyString(step.forceClick, `${prefix}.forceClick`)
  }

  if (step.targetIdForceClicked !== undefined) {
    assertNonEmptyString(
      step.targetIdForceClicked,
      `${prefix}.targetIdForceClicked`
    )
  }

  if (step.scrollIntoView !== undefined) {
    assertNonEmptyString(step.scrollIntoView, `${prefix}.scrollIntoView`)
  }

  validateOptionalBooleanField(
    step.autoScrollToTarget,
    `${prefix}.autoScrollToTarget`
  )

  validateOptionalBooleanField(
    step.allowTargetInteraction,
    `${prefix}.allowTargetInteraction`
  )

  validateOptionalBooleanField(
    step.reachableWhenTargetMissing,
    `${prefix}.reachableWhenTargetMissing`
  )

  validateOptionalGuideTourExpiresAt(step.expiresAt, `${prefix}.expiresAt`)

  if (step.featureFlag !== undefined) {
    assertNonEmptyString(step.featureFlag, `${prefix}.featureFlag`)
  }

  validateGuideTourWhenTargetMissing(step, prefix)
}
